import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './GNB.module.css';
import ChangePasswordModal from './ChangePasswordModal';

const GNB: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userName, userRole, userTeamName, logout, resetTimer } = useAuth();
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const lastResetRef = React.useRef<number>(0);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  // 활동 감지 시 타이머 초기화 (30초 간격 쓰로틀링)
  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastResetRef.current > 60000) { // 60초
      resetTimer();
      lastResetRef.current = now;
    }
  }, [resetTimer]);

  useEffect(() => {
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, [handleActivity]);

  useEffect(() => {
    const updateTimer = () => {
      const expireAtStr = sessionStorage.getItem('session_expire_at');
      if (!expireAtStr) {
        setTimeLeft(0);
        return;
      }

      const expireAt = parseInt(expireAtStr, 10);
      const remaining = Math.max(0, Math.floor((expireAt - Date.now()) / 1000));
      setTimeLeft(remaining);

      if (remaining === 0) {
        handleLogout();
      }
    };

    updateTimer(); // 초기 실행
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [handleLogout]);

  const formatTime = (seconds: number | null) => {
    if (seconds === null) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const menuItems = [
    { label: '대시보드', path: '/dashboard' },
  ];

  // 관리자 권한이 있는 경우에만 관리 탭 추가
  if (userRole === '관리자') {
    menuItems.push({ label: '관리', path: '/admin' });
  }

  return (
    <nav className={styles.gnb}>
      <div className={styles.leftSection}>
        <Link to="/dashboard" className={styles.logo}>
          Paju Weekly
        </Link>
        <ul className={styles.menu}>
          {menuItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`${styles.menuItem} ${
                  location.pathname === item.path ? styles.menuItemActive : ''
                }`}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.rightSection}>
        <div className={styles.userInfo}>
          <span className={styles.userTeam}>[{userTeamName || '소속 없음'}]</span>
          <span className={styles.userName}>{userName || '사용자'} 님</span>
          <span className={styles.timer}>{formatTime(timeLeft)}</span>
        </div>
        <div className={styles.divider} />
        <button className={styles.actionBtn} onClick={() => setIsPasswordModalOpen(true)}>
          비밀번호 변경
        </button>
        <div className={styles.divider} />
        <button
          className={`${styles.actionBtn} ${styles.logoutBtn}`}
          onClick={handleLogout}
        >
          로그아웃
        </button>
      </div>

      <ChangePasswordModal 
        isOpen={isPasswordModalOpen} 
        onClose={() => setIsPasswordModalOpen(false)} 
      />
    </nav>
  );
};

export default GNB;
